/**
 * SokoTree Product Template JavaScript
 * Handles product image gallery switching, variant selectors, reactive prices/availability, and quantity
 */

class SokoTreeProductPage {
  constructor() {
    this.container = document.querySelector('.sokotree-product-section');
    if (!this.container) return;

    this.productData = this.getProductData();
    this.initGallery();
    this.initQuantitySelectors();
    this.initVariantPills();
  }

  getProductData() {
    const jsonScript = this.container.querySelector('script[id^="ProductJson-"]');
    if (jsonScript) {
      try {
        return JSON.parse(jsonScript.textContent);
      } catch (e) {
        console.error('Error parsing product JSON:', e);
      }
    }
    return null;
  }

  initGallery() {
    const mainImg = this.container.querySelector('.js-product-main-img');
    const thumbnails = this.container.querySelectorAll('.js-gallery-thumb');

    if (!mainImg || thumbnails.length === 0) return;

    thumbnails.forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        const fullSrc = thumb.getAttribute('data-full-src');
        const altText = thumb.getAttribute('data-alt') || mainImg.alt;

        thumbnails.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');

        if (fullSrc) {
          mainImg.src = fullSrc;
          mainImg.alt = altText;
        }
      });
    });
  }

  initQuantitySelectors() {
    const minusBtn = this.container.querySelector('.js-product-qty-minus');
    const plusBtn = this.container.querySelector('.js-product-qty-plus');
    const qtyInput = this.container.querySelector('.js-product-qty-input');

    if (!qtyInput) return;

    const updateMinusState = () => {
      const val = parseInt(qtyInput.value, 10) || 1;
      if (minusBtn) {
        if (val <= 1) {
          minusBtn.classList.add('is-disabled');
          minusBtn.setAttribute('disabled', 'disabled');
        } else {
          minusBtn.classList.remove('is-disabled');
          minusBtn.removeAttribute('disabled');
        }
      }
    };

    updateMinusState();

    if (minusBtn) {
      minusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        let val = parseInt(qtyInput.value, 10) || 1;
        if (val <= 1) return;
        qtyInput.value = val - 1;
        updateMinusState();
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        let val = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = val + 1;
        updateMinusState();
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    qtyInput.addEventListener('change', () => {
      let val = parseInt(qtyInput.value, 10);
      if (isNaN(val) || val < 1) {
        qtyInput.value = 1;
      }
      updateMinusState();
    });
  }

  initVariantPills() {
    const pillGroups = this.container.querySelectorAll('.variant-pill-group');
    if (!pillGroups.length || !this.productData) return;

    const pills = this.container.querySelectorAll('.js-variant-pill');
    pills.forEach(pill => {
      pill.addEventListener('click', (e) => {
        e.preventDefault();
        const val = pill.getAttribute('data-value');

        // Update active class in group
        const parentGroup = pill.closest('.variant-pill-group');
        if (parentGroup) {
          parentGroup.querySelectorAll('.js-variant-pill').forEach(p => p.classList.remove('is-selected'));
          pill.classList.add('is-selected');

          // Update underlying select element for native form submission
          const select = parentGroup.querySelector('.js-variant-select');
          if (select) {
            select.value = val;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        this.onVariantChange();
      });
    });
  }

  onVariantChange() {
    if (!this.productData || !this.productData.variants) return;

    // Gather currently selected option values
    const selectedOptions = [];
    const pillGroups = this.container.querySelectorAll('.variant-pill-group');
    pillGroups.forEach(group => {
      const selectedPill = group.querySelector('.js-variant-pill.is-selected');
      if (selectedPill) {
        selectedOptions.push(selectedPill.getAttribute('data-value'));
      }
    });

    // Find variant matching all selected options
    const matchingVariant = this.productData.variants.find(variant => {
      return variant.options.every((optVal, idx) => optVal === selectedOptions[idx]);
    });

    if (!matchingVariant) return;

    // 1. Update Hidden Variant ID Input
    const idInput = this.container.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = matchingVariant.id;
    }

    // 2. Update Dynamic Price
    this.updatePrice(matchingVariant);

    // 3. Update Dynamic Availability Status & Add to Cart Button
    this.updateAvailability(matchingVariant);

    // 4. Update Main Image if variant has featured_media
    if (matchingVariant.featured_media && matchingVariant.featured_media.src) {
      const mainImg = this.container.querySelector('.js-product-main-img');
      if (mainImg) {
        mainImg.src = matchingVariant.featured_media.src;
      }
      const thumbnails = this.container.querySelectorAll('.js-gallery-thumb');
      thumbnails.forEach(thumb => {
        const fullSrc = thumb.getAttribute('data-full-src');
        if (fullSrc && fullSrc.includes(matchingVariant.featured_media.src)) {
          thumbnails.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        }
      });
    }

    // 5. Update URL state without reload
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('variant', matchingVariant.id);
    window.history.replaceState({ path: newUrl.href }, '', newUrl.href);
  }

  updatePrice(variant) {
    const priceContainer = this.container.querySelector('.sokotree-price-container');
    if (!priceContainer) return;

    const regularItem = priceContainer.querySelector('.price-item--regular');
    const saleItem = priceContainer.querySelector('.price-item--sale');
    const compareItem = priceContainer.querySelector('.price__sale .price-item--regular');

    const formattedPrice = this.formatMoney(variant.price);
    const onSale = variant.compare_at_price > variant.price;

    if (onSale) {
      priceContainer.classList.add('price--on-sale');
      if (saleItem) saleItem.textContent = formattedPrice;
      if (compareItem) compareItem.textContent = this.formatMoney(variant.compare_at_price);
    } else {
      priceContainer.classList.remove('price--on-sale');
      if (regularItem) regularItem.textContent = formattedPrice;
    }
  }

  updateAvailability(variant) {
    const stockWrapper = this.container.querySelector('.js-stock-status');
    const addBtn = this.container.querySelector('.product-add-to-cart-btn');
    const btnLabel = addBtn ? addBtn.querySelector('.js-btn-label') : null;

    if (variant.available) {
      if (stockWrapper) {
        stockWrapper.innerHTML = `
          <span class="stock-badge in-stock">
            <span class="stock-dot" aria-hidden="true"></span>
            <span class="stock-text">In Stock</span>
          </span>
        `;
      }
      if (addBtn) {
        addBtn.removeAttribute('disabled');
        if (btnLabel) btnLabel.textContent = 'ADD TO CART';
      }
    } else {
      if (stockWrapper) {
        stockWrapper.innerHTML = `
          <span class="stock-badge out-of-stock">
            <span class="stock-dot" aria-hidden="true"></span>
            <span class="stock-text">Out of Stock</span>
          </span>
        `;
      }
      if (addBtn) {
        addBtn.setAttribute('disabled', 'disabled');
        if (btnLabel) btnLabel.textContent = 'SOLD OUT';
      }
    }
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const format = (window.SokoTree && window.SokoTree.moneyFormat) ? window.SokoTree.moneyFormat : undefined;
      return window.Shopify.formatMoney(cents, format);
    }
    const val = (cents / 100).toFixed(2);
    return '₹' + val;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new SokoTreeProductPage();
});